import { c as _c } from "react/compiler-runtime";
import { FC } from 'react';
import { z } from 'zod';
import { useDebouncedValue, useElementSize } from '@mantine/hooks';
import { entries } from '../../collection';
import { keys } from 'remeda';
import { StyleProps } from '../../ui';
import { Rnge } from '../../range';
export const chartTypes = {
  Conditionals: {
    name: 'Conditional bars (deprecated)'
  },
  SimpleBars: {
    name: 'Simple bars'
  },
  Distributions: {
    name: 'Distributions'
  },
  Consequences: {
    name: 'Consequences'
  },
  ForecastBar: {
    name: 'Forecast squares'
  },
  CauseBars: {
    name: 'Cause bars'
  },
  Unknown: {
    name: 'Unknown'
  }
} as const;
type UnionToIntersection<union> = (union extends any ? (k: union) => void : never) extends ((k: infer intersection) => void) ? intersection : never;
type UnionToTuple<union, output extends any[] = []> = UnionToIntersection<union extends any ? (t: union) => union : never> extends ((_: any) => infer elem) ? UnionToTuple<Exclude<union, elem>, [elem, ...output]> : output;
export const zodChartType = z.enum(keys(chartTypes) as UnionToTuple<keyof typeof chartTypes>);
export type ChartType = z.infer<typeof zodChartType>;
export const allChartTypes = entries(chartTypes).map(([type, minimalMeta]) => ({
  type,
  minimalMeta
})).filter(({
  type
}) => type != 'Unknown');
export interface ChartProps {
  source?: string;
  title?: string;
}
export type ChartComponentProps<TChartProps> = {
  chart: TChartProps & ChartProps;
} & StyleProps;
export type ChartFc<T extends ChartProps = ChartProps> = FC<ChartComponentProps<T>>;
export const pallet = {
  green: '#255E4B',
  red: '#9A4564',
  yellow: '#878E5C',
  blue: '#537583',
  teal: '#31808B'
} as const;
export type ColorName = keyof typeof pallet;
export const colorNames = Object.keys(pallet) as [ColorName];
const {
  floor,
  ceil
} = Math;
export const niceWholePctBounds = (allValues: number[], nearest: number = 5) => {
  const [start, end] = Rnge.boundsOf(allValues);
  const snapToEdge = 10;
  const p = 1;
  //snap to 0/100 if within 15, otherwise to an outside whole number
  const bounds: [number, number] = [start - p < snapToEdge ? 0 : floor((start - p) / nearest) * nearest, end + p > 100 - snapToEdge ? 100 : ceil((end + p) / nearest) * nearest];
  return bounds;
};
export const useDebouncedSize = props => {
  const $ = _c(6);
  const init = props?.init;
  const debounce = props?.debounce ?? 100;
  const containerSize = useElementSize();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      leading: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [width] = useDebouncedValue(containerSize.width ? containerSize.width : init?.width ?? 0, debounce, t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      leading: true
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [height] = useDebouncedValue(containerSize.width ? containerSize.width : init?.height ?? 0, debounce, t1);
  let t2;
  if ($[2] !== containerSize || $[3] !== height || $[4] !== width) {
    t2 = {
      ...containerSize,
      width,
      height
    };
    $[2] = containerSize;
    $[3] = height;
    $[4] = width;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
};